/**
 * Sagesse design tokens — the app-facing view over the shared @sagesse/tokens package.
 *
 * The canonical source of truth is `packages/tokens` (@sagesse/tokens). This module
 * imports those values and re-shapes them into the flattened API the fan apps use
 * (tokens.brand.*, tokens.dark.*, tokens.light.*, tokens.gold, tokens.space.*). No
 * colour values are duplicated here — change a hex in @sagesse/tokens and it flows
 * through to the app automatically. This file is kept byte-identical to
 * apps/mobile/src/theme/tokens.ts so the two apps can never drift.
 *
 * ⚠ accentMint (#7FCBA8) is the interactive accent and is PENDING client sign-off
 * (decisions-log D-009). It lives in @sagesse/tokens; if the client rejects it,
 * change it once there and every consumer updates.
 *
 * TWO ACCENTS (#103): `primary` is the interactive accent (mint on dark, green on
 * light); `accent`/`accentSoft`/`accentLine`/`accentWash` are the SECONDARY voice —
 * white on dark, green on light — for the brand lockup, secondary CTAs, selected
 * states and rules. Reach for `accent*` when an element would otherwise be green
 * on green.
 */
import { color, theme, radius as radiusTokens, space as spaceScale } from '@sagesse/tokens';

export const tokens = {
  brand: color.brand,
  accentMint: color.accentMint, // ⚠ pending client approval (D-009)
  white: color.white, // dark mode's secondary accent ramp (#103)
  gold: color.gold.base, // honors / tiers
  semantic: color.semantic,
  light: {
    appBg: theme.light.appBg,
    surface: theme.light.surface,
    surface2: theme.light.surface2,
    surface3: theme.light.surface3,
    text: theme.light.text,
    textDim: theme.light.textDim,
    textFaint: theme.light.textFaint,
    border: theme.light.border,
    outline: theme.light.borderStrong,
    primary: color.brand.green,
    accent: theme.light.accent,
    accentSoft: theme.light.accentSoft,
    accentLine: theme.light.accentLine,
    accentWash: theme.light.accentWash,
    onAccent: theme.light.onAccent,
    brandMark: theme.light.brandMark,
  },
  dark: {
    appBg: theme.dark.appBg,
    surface: theme.dark.surface,
    surface2: theme.dark.surface2,
    surface3: theme.dark.surface3,
    text: theme.dark.text,
    textDim: theme.dark.textDim,
    textFaint: theme.dark.textFaint,
    border: theme.dark.border,
    outline: theme.dark.borderStrong,
    primary: color.accentMint,
    // Secondary accent is now WHITE, not brand green (#103) — a green accent on a
    // green surface never separated. Mint stays the primary interactive accent.
    accent: theme.dark.accent,
    accentSoft: theme.dark.accentSoft,
    accentLine: theme.dark.accentLine,
    accentWash: theme.dark.accentWash,
    onAccent: theme.dark.onAccent,
    brandMark: theme.dark.brandMark,
  },
  radius: radiusTokens,
  // 4-based scale aliased to the mobile naming the components already use.
  space: {
    xs: spaceScale[2], // 4
    sm: spaceScale[4], // 8
    md: spaceScale[6], // 12
    lg: spaceScale[8], // 16
    xl: spaceScale[12], // 24
    xxl: spaceScale[16], // 32
  },
} as const;
